import React, { useState, useEffect } from 'react';
import { getTeam, inviteTeammate, removeSeat, resetSeatPin, revokeInvite, regenerateInvite, disableSeat } from '../utils/crmApi';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import { getStoredAuthUser, resolveAuthUserName } from '../utils/leadOwner';

const TeamPage = () => {
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inviteValues, setInviteValues] = useState({ name: '', alias: '', role: 'agent' });
    const [inviteResult, setInviteResult] = useState(null);
    const [resetResult, setResetResult] = useState(null);
    const [authUser] = useState(() => getStoredAuthUser());

    const { showSuccess, showError, showInfo } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    const fetchTeam = async () => {
        try {
            const data = await getTeam();
            setTeam(data);
        } catch (error) {
            showError(error.message || 'Failed to load team');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeam();
    }, []);

    const buildJoinUrlFromToken = (token) => (
        token ? `${window.location.origin}/crm-portal/join?token=${token}` : ''
    );

    const copyToClipboard = async (text, message = 'Copied to clipboard') => {
        if (!text) {
            showError('Nothing to copy');
            return false;
        }
        try {
            await navigator.clipboard.writeText(text);
            showInfo(message);
            return true;
        } catch {
            showError('Clipboard access failed');
            return false;
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        try {
            const data = await inviteTeammate(inviteValues);
            const joinUrl = data?.joinUrl ? `${window.location.origin}${data.joinUrl}` : '';
            setInviteResult({
                name: inviteValues.name.trim() || inviteValues.name,
                alias: inviteValues.alias.trim() || null,
                role: inviteValues.role,
                joinUrl,
                expiresAt: data?.expiresAt || null,
            });
            if (joinUrl) {
                try {
                    await navigator.clipboard.writeText(joinUrl);
                    showSuccess('Invite created — link copied');
                } catch {
                    showSuccess('Invite created!');
                }
            } else {
                showSuccess('Invite created!');
            }
            setInviteValues({ name: '', alias: '', role: 'agent' });
            await fetchTeam();
        } catch (error) {
            showError(error.message || 'Failed to create invite');
        }
    };

    const handleRemove = async (seatId) => {
        const isConfirmed = await confirm({
            title: 'Remove User',
            message: 'Are you sure? This will revoke access immediately.',
            confirmText: 'Remove',
            variant: 'danger',
        });
        if (!isConfirmed) return;

        try {
            await removeSeat(seatId);
            showSuccess('User removed');
            fetchTeam();
        } catch (error) {
            showError(error.message || 'Failed to remove user');
        }
    };

    const handleReset = async (seatId) => {
        try {
            const data = await resetSeatPin(seatId);
            setResetResult(data);
            showSuccess('Reset link generated');
        } catch (error) {
            showError(error.message || 'Failed to reset PIN');
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;

    const fullResetUrl = resetResult ? `${window.location.origin}${resetResult.resetUrl}` : '';
    const pendingInvites = (team?.invites || [])
        .filter((invite) => {
            if (invite.status) return invite.status === 'pending';
            return !invite.usedAt && !invite.consumedAt;
        })
        .slice()
        .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
    const displaySeats = (team?.seats || []).filter((seat) => (seat.status || 'active') !== 'removed');
    const ownerName = resolveAuthUserName(authUser);

    return (
        <div className="crm-container">
            <header className="crm-page-header">
                <h1 className="crm-page-title">Team Management</h1>
                <p style={{ color: 'var(--crm-gray-500)', fontSize: '14px' }}>Manage access to the CRM Portal.</p>
            </header>

            {/* Stats */}
            {team && (
                <div className="crm-stats-grid">
                    <div className="crm-stat-card">
                        <div className="label">Seats Used</div>
                        <div className="value">{team.usage.used} / {team.usage.total}</div>
                        <div style={{ width: '100%', background: 'var(--crm-gray-100)', height: '8px', borderRadius: '4px', marginTop: '16px' }}>
                            <div style={{
                                width: `${(team.usage.used / team.usage.total) * 100}%`,
                                background: 'var(--crm-primary)',
                                height: '100%',
                                borderRadius: '4px'
                            }} />
                        </div>
                    </div>
                </div>
            )}

            {/* Invite Section */}
            <div className="crm-card" style={{ marginBottom: '24px' }}>
                <div className="crm-card-header">
                    <h2 className="crm-card-title">Invite Teammate</h2>
                </div>
                <div className="crm-card-body">
                    <p className="crm-team-invite-intro">
                        Create an invite and share the link. Your teammate will use that link to set their own PIN and activate their CRM Portal access.
                    </p>
                    <form onSubmit={handleInvite} className="crm-team-invite-form">
                        <div className="crm-team-invite-field">
                            <label className="crm-label">Name</label>
                            <input
                                type="text"
                                required
                                className="crm-input"
                                value={inviteValues.name}
                                onChange={e => setInviteValues({ ...inviteValues, name: e.target.value })}
                            />
                        </div>
                        <div className="crm-team-invite-field">
                            <label className="crm-label">Username (optional)</label>
                            <input
                                type="text"
                                placeholder="Auto-generated if left blank"
                                className="crm-input"
                                value={inviteValues.alias}
                                onChange={e => setInviteValues({ ...inviteValues, alias: e.target.value })}
                            />
                        </div>
                        <div className="crm-team-invite-field crm-team-invite-role">
                            <label className="crm-label">Role</label>
                            <select
                                className="crm-select"
                                value={inviteValues.role}
                                onChange={e => setInviteValues({ ...inviteValues, role: e.target.value })}
                            >
                                <option value="agent">Agent</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                        <div className="crm-team-invite-helper">
                            This is the login username shown on the CRM Portal login screen.
                        </div>
                        <button
                            type="submit"
                            disabled={team?.usage?.remaining === 0}
                            className="crm-btn crm-btn-primary crm-team-invite-submit"
                        >
                            Create Invite
                        </button>
                    </form>
                    <div className="crm-team-invite-tip">
                        Use <strong>Copy Link</strong> to resend the same invite. Use <strong>Regenerate Link</strong> only if the old link was lost or shared incorrectly.
                    </div>
                </div>
            </div>

            {/* Pending Invites */}
            <div className="crm-card">
                <div className="crm-card-header">
                    <h2 className="crm-card-title">Pending Invites</h2>
                </div>
                <div className="crm-table-container">
                    <table className="crm-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Expires</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingInvites.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="cell-secondary">No pending invites</td>
                                </tr>
                            ) : (
                                pendingInvites.map((invite) => {
                                    const inviteKey = invite.inviteId || invite.tokenHash || invite.token || invite.createdAt;
                                    const canManage = !!invite.inviteId;
                                    const stableJoinUrl = buildJoinUrlFromToken(invite.token);
                                    return (
                                    <tr key={inviteKey}>
                                        <td>
                                            <div className="cell-main">{invite.name}</div>
                                            <div className="cell-secondary">
                                                {invite.alias ? `Login: ${invite.alias}` : 'Login: (auto)'}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="crm-badge crm-badge-customer" style={{ textTransform: 'capitalize' }}>
                                                {invite.role}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="crm-badge crm-badge-prospect">Invited</span>
                                        </td>
                                        <td className="cell-secondary">
                                            {invite.createdAt ? new Date(invite.createdAt).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="cell-secondary">
                                            {invite.expiresAt ? new Date(invite.expiresAt).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="cell-actions">
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                {invite.alias && (
                                                    <button
                                                        onClick={() => copyToClipboard(invite.alias, 'Login username copied')}
                                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                                    >
                                                        Copy Login
                                                    </button>
                                                )}
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            if (stableJoinUrl) {
                                                                const copied = await copyToClipboard(stableJoinUrl, 'Invite link copied');
                                                                if (copied) {
                                                                    setInviteResult({
                                                                        name: invite.name,
                                                                        alias: invite.alias || null,
                                                                        role: invite.role || 'agent',
                                                                        joinUrl: stableJoinUrl,
                                                                        expiresAt: invite.expiresAt || null,
                                                                    });
                                                                }
                                                                return;
                                                            }
                                                            const confirmed = await confirm({
                                                                title: 'Generate a New Invite Link?',
                                                                message: 'This older invite does not have a stored link to copy. A new invite link will be generated and the previous link will stop working.',
                                                                confirmText: 'Generate New Link',
                                                            });
                                                            if (!confirmed) return;
                                                            const data = await regenerateInvite(invite.inviteId);
                                                            const joinUrl = `${window.location.origin}${data.joinUrl}`;
                                                            const copied = await copyToClipboard(joinUrl, 'New invite link copied');
                                                            setInviteResult({
                                                                name: invite.name,
                                                                alias: invite.alias || null,
                                                                role: invite.role || 'agent',
                                                                joinUrl,
                                                                expiresAt: data?.expiresAt || invite.expiresAt || null,
                                                            });
                                                            showSuccess(copied ? 'New invite link generated and copied' : 'New invite link generated');
                                                            await fetchTeam();
                                                        } catch (error) {
                                                            showError(error.message || 'Failed to copy invite link');
                                                        }
                                                    }}
                                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                                    disabled={!canManage}
                                                >
                                                    Copy Link
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        const confirmed = await confirm({
                                                            title: 'Regenerate Invite Link',
                                                            message: 'This invalidates the current link and creates a new one. Continue?',
                                                            confirmText: 'Regenerate',
                                                        });
                                                        if (!confirmed) return;
                                                        try {
                                                            const data = await regenerateInvite(invite.inviteId);
                                                            const joinUrl = `${window.location.origin}${data.joinUrl}`;
                                                            setInviteResult({
                                                                name: invite.name,
                                                                alias: invite.alias || null,
                                                                role: invite.role || 'agent',
                                                                joinUrl,
                                                                expiresAt: data?.expiresAt || null,
                                                            });
                                                            showSuccess('New invite link generated');
                                                            await fetchTeam();
                                                        } catch (error) {
                                                            showError(error.message || 'Failed to regenerate invite');
                                                        }
                                                    }}
                                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                                    disabled={!canManage}
                                                >
                                                    Regenerate Link
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            await revokeInvite(invite.inviteId);
                                                            await fetchTeam();
                                                            showSuccess('Invite revoked');
                                                        } catch (error) {
                                                            showError(error.message || 'Failed to revoke invite');
                                                        }
                                                    }}
                                                    className="crm-btn crm-btn-danger crm-btn-sm"
                                                    disabled={!canManage}
                                                >
                                                    Revoke
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Active Seats */}
            <div className="crm-card" style={{ marginTop: '24px' }}>
                <div className="crm-card-header">
                    <h2 className="crm-card-title">Active Seats</h2>
                </div>
                <div className="crm-table-container">
                    <table className="crm-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Last Seen</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displaySeats.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="cell-secondary">No active seats</td>
                                </tr>
                            ) : (
                                displaySeats.map((seat) => {
                                    const displayName = seat.id === 'seat_admin' && ownerName ? ownerName : seat.name;
                                    const seatStatus = seat.status || 'active';
                                    return (
                                        <tr key={seat.id}>
                                            <td>
                                                <div className="cell-main">{displayName}</div>
                                                <div className="cell-secondary">
                                                    {seat.alias ? `Login: ${seat.alias}` : seat.id}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`crm-badge ${seat.role === 'admin' ? 'crm-badge-prospect' : 'crm-badge-customer'}`} style={{ textTransform: 'capitalize' }}>
                                                    {seat.role}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`crm-badge ${seatStatus === 'disabled' ? 'crm-badge-prospect' : 'crm-badge-customer'}`}>
                                                    {seatStatus === 'disabled' ? 'Disabled' : 'Active'}
                                                </span>
                                            </td>
                                            <td className="cell-secondary">
                                                {seat.joinedAt ? new Date(seat.joinedAt).toLocaleDateString() : new Date(seat.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="cell-secondary">
                                                {seat.lastSeenAt ? new Date(seat.lastSeenAt).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="cell-actions">
                                                {(seat.id !== 'seat_admin' && seat.role !== 'admin') && (
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                        {seat.alias && (
                                                            <button
                                                                onClick={() => copyToClipboard(seat.alias, 'Login username copied')}
                                                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                                            >
                                                                Copy Login
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    await disableSeat(seat.id);
                                                                    await fetchTeam();
                                                                    showSuccess('Seat disabled');
                                                                } catch (error) {
                                                                    showError(error.message || 'Failed to disable seat');
                                                                }
                                                            }}
                                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                                            disabled={seatStatus === 'disabled'}
                                                        >
                                                            Disable
                                                        </button>
                                                        <button
                                                            onClick={() => handleReset(seat.id)}
                                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                                        >
                                                            Reset PIN
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemove(seat.id)}
                                                            className="crm-btn crm-btn-danger crm-btn-sm"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                                {seat.id === 'seat_admin' && (
                                                    <span style={{ fontSize: '12px', color: 'var(--crm-gray-400)', fontStyle: 'italic' }}>Main Account</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Invite Share Modal / Display */}
            {inviteResult && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }}>
                    <div className="crm-card" style={{ width: '100%', maxWidth: '560px', padding: '0' }}>
                        <div className="crm-card-header">
                            <h3 className="crm-card-title">Invite Ready to Share</h3>
                        </div>
                        <div className="crm-card-body">
                            <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
                                <div style={{ color: 'var(--crm-gray-600)', fontSize: '13px' }}>
                                    Share this link with <strong style={{ color: 'var(--crm-gray-900)' }}>{inviteResult.name}</strong>. They will use it once to set their CRM Portal PIN.
                                </div>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--crm-gray-500)' }}>
                                    <span>Role: <strong style={{ textTransform: 'capitalize', color: 'var(--crm-gray-700)' }}>{inviteResult.role}</strong></span>
                                    <span>Login: <strong style={{ color: 'var(--crm-gray-700)' }}>{inviteResult.alias || 'Auto-generated'}</strong></span>
                                    {inviteResult.expiresAt && (
                                        <span>Expires: <strong style={{ color: 'var(--crm-gray-700)' }}>{new Date(inviteResult.expiresAt).toLocaleString()}</strong></span>
                                    )}
                                </div>
                            </div>
                            <div style={{ background: 'var(--crm-gray-100)', padding: '12px', border: '1px solid var(--crm-gray-200)', wordBreak: 'break-all', fontSize: '13px', marginBottom: '12px', color: 'var(--crm-gray-900)' }}>
                                {inviteResult.joinUrl}
                            </div>
                            <p style={{ margin: '0 0 16px', fontSize: '12px', color: 'var(--crm-gray-500)' }}>
                                Regenerate the invite link only if the old link was lost, expired, or sent to the wrong person.
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                                {inviteResult.alias && (
                                    <button
                                        onClick={() => copyToClipboard(inviteResult.alias, 'Login username copied')}
                                        className="crm-btn crm-btn-secondary"
                                    >
                                        Copy Login
                                    </button>
                                )}
                                <button
                                    onClick={() => copyToClipboard(inviteResult.joinUrl, 'Invite link copied')}
                                    className="crm-btn crm-btn-primary"
                                >
                                    Copy Invite Link
                                </button>
                                <button
                                    onClick={() => setInviteResult(null)}
                                    className="crm-btn crm-btn-secondary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Modal / Display */}
            {resetResult && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }}>
                    <div className="crm-card" style={{ width: '100%', maxWidth: '480px', padding: '0' }}>
                        <div className="crm-card-header">
                            <h3 className="crm-card-title">PIN Reset Link Generated</h3>
                        </div>
                        <div className="crm-card-body">
                            <p style={{ color: 'var(--crm-gray-600)', marginBottom: '16px' }}>
                                Send this link to the user. It will expire in 1 hour.
                            </p>
                            <div style={{ background: 'var(--crm-gray-100)', padding: '12px', borderRadius: 'var(--crm-border-radius-sm)', border: '1px solid var(--crm-gray-200)', wordBreak: 'break-all', fontSize: '13px', marginBottom: '24px', color: 'var(--crm-gray-900)' }}>
                                {fullResetUrl}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button
                                    onClick={() => copyToClipboard(fullResetUrl, 'Reset link copied')}
                                    className="crm-btn crm-btn-primary"
                                >
                                    Copy Link
                                </button>
                                <button
                                    onClick={() => setResetResult(null)}
                                    className="crm-btn crm-btn-secondary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialog />
        </div>
    );
};

export default TeamPage;
